import { merge } from 'lodash';
import type { App, AppActions, ActionsProvider, LooseAppLoaderConfig, TightAppLoaderConfig, AppConfig } from '@nab/x-types';
import { getShellServices } from '../services';
import { buildUser, setUserConfig, getUserForShell } from '../user';

const actionsProviders: ActionsProvider[] = [];
let shellActions: AppActions;

export const registerActionsProvider = (provider: ActionsProvider): void => {
    actionsProviders.push(provider);
};

export const getActionsProviders = (): ActionsProvider[] => actionsProviders;

/**
 * Creates a set of action functions for a specific app instance
 */
export const createActionsForAppInstance = (appMetaData: App, instanceId: string, defaultActionsProvider: ActionsProvider): AppActions => {
    let actions: AppActions = defaultActionsProvider(appMetaData, instanceId) as AppActions;

    // Add all registered actions providers
    for (const ap of actionsProviders) {
        actions = {
            ...actions,
            ...ap(appMetaData, instanceId)
        };
    }

    return actions;
};

export const setShellActions = (actions: AppActions) => {
    shellActions = actions;
};

export const getShellActions = (): AppActions => {
    if (typeof shellActions !== 'object') {
        throw new Error(`x-runtime has not been initialised. Please initialise it using the initRuntime function`);
    }
    return shellActions;
};

export const resetActionsRegistry = () => {
    shellActions = undefined;
    actionsProviders.splice(0, actionsProviders.length);
};

/**
 * 1) set data into the central user config. 2) update services. 3) build new user
 * @param rootAppConfig used to build new user
 * @param app used to verify which app is calling this function. Also used to build new user
 * @param data used to: 1) set data into the central user config. 2) update services. 3) build new user
 * @returns Promise<LooseAppLoaderConfig>
 */
export const baseUpdateAction = async (rootAppConfig: AppConfig, app: App, data: LooseAppLoaderConfig): Promise<TightAppLoaderConfig> => {
    // Only shell can update apps data!
    if (app.id !== 'SHELL') {
        throw new Error(`Only shell can update apps data!`);
    }

    const shellServices = getShellServices();
    let appLoaderConfig = { ...data } as TightAppLoaderConfig;
    const sessionId = appLoaderConfig.user?.sessionId || appLoaderConfig.appConfig?.sessionId;
    if (sessionId) {
        appLoaderConfig = merge({}, appLoaderConfig, { appConfig: { sessionId }, user: { sessionId } });
    }

    if (appLoaderConfig.user) {
        // Update userConfig
        const userConfig = await setUserConfig(appLoaderConfig.user);

        // Update services
        shellServices.dataSecurityMgr.fhp.updateData({
            ...(sessionId && { csId: userConfig.sessionId }),
            ...(appLoaderConfig.user.id && { userId: userConfig.id }),
            ...(typeof appLoaderConfig.user.authenticated !== 'undefined' && { authenticated: userConfig.authenticated })
        });
        shellServices.dataSecurityMgr.bc.updateData(userConfig.sessionId);

        // Update shellUser
        const newShellUser = buildUser(
            {},
            {
                appMetaData: app,
                appConfig: {
                    ...rootAppConfig,
                    ...appLoaderConfig.appConfig
                }
            },
            shellServices.analyticsMgr,
            shellServices.dataSecurityMgr,
            shellServices.flagMgr,
            shellServices.rumMgr,
            shellServices.interactionMgr
        );

        appLoaderConfig.user = newShellUser;
    } else {
        appLoaderConfig.user = getUserForShell();
    }

    appLoaderConfig.services = shellServices;
    appLoaderConfig.actions = shellActions;

    return appLoaderConfig;
};
